import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

// ── Discipline scope multipliers (from Excel: "una sola disciplina.xlsx") ────
// When fewer than all disciplines are contracted, apply this factor to base price.
export const DISCIPLINE_SCOPE_MULTIPLIERS = {
    FL_RES: { full: 1.00, arch_struct: 0.80,  arch_mep: 0.80,  arch_civil: 0.80,  struct_civil: 0.80,  mep_civil: 0.80,  solo_arch: 0.45, solo_struct: 0.35, solo_mep: 0.35, solo_civil: 0.35 },
    FL_COM: { full: 1.00, arch_struct: 0.92,  arch_mep: 0.92,  arch_civil: 0.92,  struct_civil: 0.92,  mep_civil: 0.92,  solo_arch: 0.68, solo_struct: 0.61, solo_mep: 0.61, solo_civil: 0.61 },
    TX_RES: { full: 1.00, arch_struct: 0.921, arch_mep: 0.921, arch_civil: 0.921, struct_civil: 0.921, mep_civil: 0.921, solo_arch: 0.68, solo_struct: 0.62, solo_mep: 0.62, solo_civil: 0.62 },
    TX_COM: { full: 1.00, arch_struct: 0.92,  arch_mep: 0.92,  arch_civil: 0.92,  struct_civil: 0.92,  mep_civil: 0.92,  solo_arch: 0.68, solo_struct: 0.62, solo_mep: 0.62, solo_civil: 0.62 },
    FL_CIVIL: { full: 1.00, arch_struct: 0.92, arch_mep: 0.92, arch_civil: 0.92, struct_civil: 0.92, mep_civil: 0.92, solo_arch: 0.68, solo_struct: 0.61, solo_mep: 0.61, solo_civil: 0.61 },
    TX_CIVIL: { full: 1.00, arch_struct: 0.92, arch_mep: 0.92, arch_civil: 0.92, struct_civil: 0.92, mep_civil: 0.92, solo_arch: 0.68, solo_struct: 0.62, solo_mep: 0.62, solo_civil: 0.62 },
}

export const DISCIPLINE_SCOPE_OPTIONS = [
    { key: 'full',         labelKey: 'admin.arch.scope_full' },
    { key: 'arch_struct',  labelKey: 'admin.arch.scope_arch_struct' },
    { key: 'arch_mep',     labelKey: 'admin.arch.scope_arch_mep' },
    { key: 'arch_civil',   labelKey: 'admin.arch.scope_arch_civil' },
    { key: 'struct_civil', labelKey: 'admin.arch.scope_struct_civil' },
    { key: 'mep_civil',    labelKey: 'admin.arch.scope_mep_civil' },
    { key: 'solo_arch',    labelKey: 'admin.arch.scope_solo_arch' },
    { key: 'solo_struct',  labelKey: 'admin.arch.scope_solo_struct' },
    { key: 'solo_mep',     labelKey: 'admin.arch.scope_solo_mep' },
    { key: 'solo_civil',   labelKey: 'admin.arch.scope_solo_civil' },
]

// Default percentage split for each scope option (arch / struct / mep / civil / admin)
export const SCOPE_OPTION_TO_PCT = {
    full:         { archPct: 50,  structPct: 22, mepPct: 20,  civilPct: 0,  adminPct: 8  },
    arch_struct:  { archPct: 60,  structPct: 32, mepPct: 0,  civilPct: 0,  adminPct: 8  },
    arch_mep:     { archPct: 50,  structPct: 0,  mepPct: 42, civilPct: 0,  adminPct: 8  },
    arch_civil:   { archPct: 50,  structPct: 0,  mepPct: 0,  civilPct: 42, adminPct: 8  },
    struct_civil: { archPct: 0,   structPct: 50, mepPct: 0,  civilPct: 42, adminPct: 8  },
    mep_civil:    { archPct: 0,   structPct: 0,  mepPct: 50, civilPct: 42, adminPct: 8  },
    solo_arch:    { archPct: 92,  structPct: 0,  mepPct: 0,  civilPct: 0,  adminPct: 8  },
    solo_struct:  { archPct: 0,   structPct: 92, mepPct: 0,  civilPct: 0,  adminPct: 8  },
    solo_mep:     { archPct: 0,   structPct: 0,  mepPct: 92, civilPct: 0,  adminPct: 8  },
    solo_civil:   { archPct: 0,   structPct: 0,  mepPct: 0,  civilPct: 92, adminPct: 8  },
}

// ── Difficulty / complexity levels ────────────────────────────────────────────
export const DIFFICULTY_LEVELS = [
    { value: 1.0, label: 'Base',             desc: 'Standard',           labelKey: 'admin.arch.diff_base',     descKey: 'admin.arch.diff_base_desc' },
    { value: 1.2, label: 'x1.2',             desc: 'Somewhat complicated addition', labelKey: 'admin.arch.diff_1_2', descKey: 'admin.arch.diff_1_2_desc' },
    { value: 1.3, label: 'x1.3',             desc: 'Flood zone / footing', labelKey: 'admin.arch.diff_1_3',    descKey: 'admin.arch.diff_1_3_desc' },
    { value: 1.5, label: 'x1.5',             desc: '2-story in flood zone', labelKey: 'admin.arch.diff_1_5',   descKey: 'admin.arch.diff_1_5_desc' },
    { value: 1.6, label: 'x1.6',             desc: '2–3 story modern',    labelKey: 'admin.arch.diff_1_6',     descKey: 'admin.arch.diff_1_6_desc' },
    { value: 1.7, label: 'x1.7',             desc: 'Office / daycare / church', labelKey: 'admin.arch.diff_1_7', descKey: 'admin.arch.diff_1_7_desc' },
    { value: 1.8, label: 'x1.8',             desc: 'Multi-story commercial / mixed-use', labelKey: 'admin.arch.diff_1_8', descKey: 'admin.arch.diff_1_8_desc' },
    { value: 1.9, label: 'x1.9',             desc: 'Restaurant',          labelKey: 'admin.arch.diff_1_9',     descKey: 'admin.arch.diff_1_9_desc' },
]

// ── Disciplines ──────────────────────────────────────────────────────────────
export const ARCH_DISCIPLINES = ['ARCH', 'STRUCT', 'MEP', 'CIVIL', 'ECC', 'ADMIN']

// ── Discipline normalization (canonical short codes) ─────────────────────────
export const DISC_NORMALIZE = {
    ARCHITECTURE: 'ARCH', ARCH: 'ARCH',
    STRUCTURAL: 'STRUCT', STRUCT: 'STRUCT', ESTRUCTURAL: 'STRUCT',
    MEP: 'MEP', MECHANICAL: 'MEP', ELECTRICAL: 'MEP', PLUMBING: 'MEP',
    CIVIL: 'CIVIL', SITE: 'CIVIL', CIVIL_DEV: 'CIVIL',
    ECC: 'ECC', ENERGY: 'ECC',
    ADMIN: 'ADMIN', PM: 'ADMIN', 'PROJECT MANAGEMENT': 'ADMIN', MANAGEMENT: 'ADMIN',
}
export function normalizeDiscipline(d) {
    return DISC_NORMALIZE[(d || '').toUpperCase().trim()] || (d || 'GENERAL').toUpperCase()
}

// ── Catalog phase tracks ──────────────────────────────────────────────────────
export const ARCH_PHASES = [
    { key: 'cds',    labelKey: 'admin.arch.phase_cds' },
    { key: 'design', labelKey: 'admin.arch.phase_design' },
]

// ── Project types ─────────────────────────────────────────────────────────────
export const ARCH_PROJECT_TYPES = [
    { key: 'res_new',     labelKey: 'admin.arch.ptype_res_new' },
    { key: 'res_add_alt', labelKey: 'admin.arch.ptype_res_add_alt' },
    { key: 'res_rem',     labelKey: 'admin.arch.ptype_res_rem' },
    { key: 'com_new',     labelKey: 'admin.arch.ptype_com_new' },
    { key: 'com_add_alt', labelKey: 'admin.arch.ptype_com_add_alt' },
    { key: 'com_rem',     labelKey: 'admin.arch.ptype_com_rem' },
    { key: 'civil',       labelKey: 'admin.arch.ptype_civil' },
    { key: 'addon',       labelKey: 'admin.arch.ptype_addon' },
]

// ── Market labels ─────────────────────────────────────────────────────────────
export const MARKET_OPTIONS = [
    { key: 'FL_RES', state: 'FL', type: 'RES', labelKey: 'admin.arch.market_fl_res' },
    { key: 'FL_COM', state: 'FL', type: 'COM', labelKey: 'admin.arch.market_fl_com' },
    { key: 'TX_RES', state: 'TX', type: 'RES', labelKey: 'admin.arch.market_tx_res' },
    { key: 'TX_COM', state: 'TX', type: 'COM', labelKey: 'admin.arch.market_tx_com' },
    { key: 'FL_CIVIL', state: 'FL', type: 'CIVIL', labelKey: 'admin.arch.market_fl_civil' },
    { key: 'TX_CIVIL', state: 'TX', type: 'CIVIL', labelKey: 'admin.arch.market_tx_civil' },
]

// ── Internal cost role breakdowns (defaults, % of total contract) ─────────────
export const DIRECT_COST_ROLES = [
    { roleKey: 'arch_drafter',   discipline: 'ARCH',   labelKey: 'admin.arch.role_arch_drafter',    pct: 25.0,   is_enabled: true },
    { roleKey: 'struct_drafter', discipline: 'STRUCT', labelKey: 'admin.arch.role_struct_drafter',  pct: 25.0,   is_enabled: true },
    { roleKey: 'mech_drafter',   discipline: 'MEP',    labelKey: 'admin.arch.role_mech_drafter',    pct: 10.0,   is_enabled: true },
    { roleKey: 'elec_drafter',   discipline: 'MEP',    labelKey: 'admin.arch.role_elec_drafter',    pct: 10.0,   is_enabled: true },
    { roleKey: 'plumb_drafter',  discipline: 'MEP',    labelKey: 'admin.arch.role_plumb_drafter',   pct: 10.0,   is_enabled: true },
    { roleKey: 'pm',             discipline: 'ALL',    labelKey: 'admin.arch.role_pm',              pct: 8.5,    is_enabled: true },
    { roleKey: 'tech_manager',   discipline: 'ALL',    labelKey: 'admin.arch.role_tech_manager',    pct: 8.5,    is_enabled: true },
    { roleKey: 'prod_manager',   discipline: 'ALL',    labelKey: 'admin.arch.role_prod_manager',    pct: 3.0,    is_enabled: true },
]

export const CERTIFIER_ROLES = [
    { roleKey: 'pe_arch',   discipline: 'ARCH',   labelKey: 'admin.arch.role_pe_arch',     pct: 40.0,   is_enabled: true },
    { roleKey: 'pe_struct', discipline: 'STRUCT', labelKey: 'admin.arch.role_pe_struct',   pct: 30.0,   is_enabled: true },
    { roleKey: 'pe_mep',    discipline: 'MEP',    labelKey: 'admin.arch.role_pe_mep',      pct: 30.0,   is_enabled: true },
]

export const COMMISSION_ROLES = [
    { roleKey: 'trafficker',  discipline: 'ALL', labelKey: 'admin.arch.role_trafficker',        pct: 20.0,   is_enabled: true },
    { roleKey: 'sales_coach', discipline: 'ALL', labelKey: 'admin.arch.role_sales_coach',       pct: 10.0,   is_enabled: true },
    { roleKey: 'salesperson', discipline: 'ALL', labelKey: 'admin.arch.role_salesperson',       pct: 32.5,   is_enabled: true },
    { roleKey: 'estimator',   discipline: 'ALL', labelKey: 'admin.arch.role_estimator',         pct: 22.5,   is_enabled: true },
    { roleKey: 'biller',      discipline: 'ALL', labelKey: 'admin.arch.role_biller',            pct: 15.0,   is_enabled: true },
]

export const OPERATING_ROLES = [
    { roleKey: 'office_rent',       discipline: 'ALL', labelKey: 'admin.arch.role_office_rent',    pct: 100.0, is_enabled: true },
]

export const FRANCHISE_ROLES = [
    { roleKey: 'franchise_fee',     discipline: 'ALL', labelKey: 'admin.arch.role_franchise_fee',  pct: 100.0, is_enabled: true },
]

export const PROFIT_ROLES = [
    { roleKey: 'target_profit',     discipline: 'ALL', labelKey: 'admin.arch.role_target_profit',  pct: 100.0, is_enabled: true },
]

export const CONTINGENCY_ROLES = [
    { roleKey: 'contingency_buffer', discipline: 'ALL', labelKey: 'admin.arch.role_contingency',   pct: 100.0, is_enabled: true },
]

// ── Default cuotas ────────────────────────────────────────────────────────────
export const DEFAULT_CUOTAS = [
    { labelKey: 'admin.arch.cuota_30_initial', pct: 30 },
    { labelKey: 'admin.arch.cuota_40_permit',  pct: 40 },
    { labelKey: 'admin.arch.cuota_20_completion', pct: 20 },
    { labelKey: 'admin.arch.cuota_10_final',   pct: 10 },
]

// ── Payment schedule presets for arch estimator ───────────────────────────────
export const ARCH_SCHEDULE_PRESETS = [
    { nameKey: 'admin.arch.schedule_arch_35_35_27_3', rows: [
        { labelKey: 'admin.arch.payment_35_retainer',       pct: 35 },
        { labelKey: 'admin.arch.payment_35_design_dev',     pct: 35 },
        { labelKey: 'admin.arch.payment_27_permit_sub',     pct: 27 },
        { labelKey: 'admin.arch.payment_3_approval',        pct: 3  },
    ]},
    { nameKey: 'admin.arch.schedule_standard_30_40_20_10', rows: [
        { labelKey: 'admin.arch.payment_30_initial',        pct: 30 },
        { labelKey: 'admin.arch.payment_40_permit',         pct: 40 },
        { labelKey: 'admin.arch.payment_20_completion',     pct: 20 },
        { labelKey: 'admin.arch.payment_10_final',          pct: 10 },
    ]},
    { nameKey: 'admin.arch.schedule_50_50', rows: [
        { labelKey: 'admin.arch.payment_50_deposit',        pct: 50 },
        { labelKey: 'admin.arch.payment_50_completion',     pct: 50 },
    ]},
    { nameKey: 'admin.arch.schedule_thirds', rows: [
        { labelKey: 'admin.arch.payment_33_initial',        pct: 33 },
        { labelKey: 'admin.arch.payment_33_midway',         pct: 33 },
        { labelKey: 'admin.arch.payment_34_final',          pct: 34 },
    ]},
    { nameKey: 'admin.arch.schedule_40_30_30', rows: [
        { labelKey: 'admin.arch.payment_40_initial',        pct: 40 },
        { labelKey: 'admin.arch.payment_30_permit',         pct: 30 },
        { labelKey: 'admin.arch.payment_30_completion',     pct: 30 },
    ]},
]

// ── Fallback base prices (used before Supabase loads) ─────────────────────────
export const DEFAULT_BASE_PRICES = {
    FL_RES: 4.65,
    FL_COM: 3.81,
    TX_RES: 1.90,
    TX_COM: 2.71,
    FL_CIVIL: 0.2,
    TX_CIVIL: 0.2,
}

// ── Add/Alt & Remodel base rates (source: Excel "CALCULADORA DE COMISIONES" Nov 2024) ───
export const ADD_ALT_BASE_PRICES = {
    FL_RES: 2.5431,
    FL_COM: 4.4417,
    TX_RES: 1.9000,
    TX_COM: 3.1181,
    TX_CIVIL: 0,
}

// Minimum price floor per component (in $)
export const ADD_ALT_MIN_FLOORS = {
    exist:   1200,
    remodel: 250,
    adicion: 200,
}

// Default difficulty multipliers per state for Add/Alt projects
export const ADD_ALT_DIFFICULTY_DEFAULTS = {
    FL: { exist: 0.125, remodel: 1.0, adicion: 1.0 },
    TX: { exist: 0.20,  remodel: 0.4, adicion: 1.0 },
}

// Project type keys that use the Add/Alt 3-component calculator
export const ADD_ALT_PROJECT_TYPES = new Set(['res_add_alt', 'res_rem', 'com_add_alt', 'com_rem'])

const COST_DEFAULTS = {
    add_alt_exist_base_price: 0,
    add_alt_rem_base_price:   0,
    add_alt_add_base_price:   0,
    direct_cost_pct:          0.55,
    certifier_pct:            0.07,
    operating_pct:            0.12,
    sales_pct:                0.05,
    franchise_pct:            0.03,
    profit_pct:               0.13,
    contingency_pct:          0.05,
    min_floor_exist:          ADD_ALT_MIN_FLOORS.exist,
    min_floor_remodel:        ADD_ALT_MIN_FLOORS.remodel,
    min_floor_adicion:        ADD_ALT_MIN_FLOORS.adicion,
}

// ── Robust COM/RES prefix from any project type key ────────────────────────
export function getProjectTypeComResPrefix(projectType) {
    const t = String(projectType || '').toLowerCase()
    if (t.startsWith('com') || t.includes('commercial')) return 'com'
    if (t.startsWith('res') || t.includes('residential')) return 'res'
    return null
}

// ── Monday → project_type resolver ──────────────────────────────────────────
export function resolveProjectTypeKey(typeTxt, descTxt) {
    const t = (typeTxt || '').toLowerCase()
    const d = (descTxt || '').toLowerCase()
    if (d.includes('site visit') && !d.includes('dev'))   return 'addon'
    if (d.includes('inspection'))                          return 'addon'
    if (t.includes('civil'))                               return 'civil'
    const isRes = t.includes('resid')
    if (d.includes('new'))  return isRes ? 'res_new'     : 'com_new'
    if (d.includes('add'))  return isRes ? 'res_add_alt' : 'com_add_alt'
    if (d.includes('rem'))  return isRes ? 'res_rem'     : 'com_rem'
    if (d.includes('dev'))  return 'civil'
    return null
}

// ── Derive discipline split suggestion from populated scope items ───────────
export function deriveDisciplineSuggestion(scopeItems) {
    const discs = new Set((scopeItems || []).map(i => (i.discipline || '').toUpperCase()))
    const hasArch   = discs.has('ARCH')
    const hasStruct = discs.has('STRUCT')
    const hasMep    = discs.has('MEP')
    const hasCivil  = discs.has('CIVIL')
    // Combinations with civil
    if (hasArch && hasStruct && hasMep && hasCivil)  return { scope: 'full',         archPct: 45,  structPct: 20, mepPct: 18,  civilPct: 9,  adminPct: 8  }
    if (hasArch && hasStruct && hasMep)              return { scope: 'full',         archPct: 50,  structPct: 22, mepPct: 20,  civilPct: 0,  adminPct: 8  }
    if (hasArch && hasStruct && hasCivil)            return { scope: 'full',         archPct: 42,  structPct: 25, mepPct: 0,   civilPct: 25, adminPct: 8  }
    if (hasArch && hasMep && hasCivil)               return { scope: 'full',         archPct: 42,  structPct: 0,  mepPct: 35,  civilPct: 15, adminPct: 8  }
    if (hasStruct && hasMep && hasCivil)             return { scope: 'full',         archPct: 0,   structPct: 30, mepPct: 32,  civilPct: 30, adminPct: 8  }
    if (hasArch && hasCivil && !hasStruct && !hasMep) return { scope: 'arch_civil',   archPct: 50,  structPct: 0,  mepPct: 0,   civilPct: 42, adminPct: 8  }
    if (hasStruct && hasCivil && !hasArch && !hasMep) return { scope: 'struct_civil', archPct: 0,   structPct: 50,mepPct: 0,   civilPct: 42, adminPct: 8  }
    if (hasMep && hasCivil && !hasArch && !hasStruct) return { scope: 'mep_civil',    archPct: 0,   structPct: 0,  mepPct: 50,  civilPct: 42, adminPct: 8  }
    if (hasCivil && !hasArch && !hasStruct && !hasMep) return { scope: 'solo_civil',   archPct: 0,   structPct: 0,  mepPct: 0,   civilPct: 92, adminPct: 8  }
    if (hasArch && hasStruct)                        return { scope: 'arch_struct',  archPct: 60,  structPct: 32, mepPct: 0,   civilPct: 0,  adminPct: 8  }
    if (hasArch && !hasStruct && !hasMep && !hasCivil) return { scope: 'solo_arch',    archPct: 92,  structPct: 0,  mepPct: 0,   civilPct: 0,  adminPct: 8  }
    if (hasStruct && !hasArch && !hasMep && !hasCivil) return { scope: 'solo_struct',  archPct: 0,   structPct: 92, mepPct: 0,  civilPct: 0,  adminPct: 8  }
    if (hasMep && !hasArch && !hasStruct && !hasCivil) return { scope: 'solo_mep',     archPct: 0,   structPct: 0,  mepPct: 92,  civilPct: 0,  adminPct: 8  }
    return { scope: 'full', archPct: 50, structPct: 22, mepPct: 20, civilPct: 0, adminPct: 8 }
}

/**
 * useArchConfig — loads all arch_config rows from Supabase.
 *
 * Returns:
 *   rows             — raw DB rows
 *   loading / error
 *   getMarketConfig  — (marketKey) => merged config object with defaults
 *   saveMarketConfig — (marketKey, updates) => upserts row
 *   reload
 */
export function useArchConfig() {
    const [rows,    setRows]    = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        // Serve from cache immediately (stale-while-revalidate)
        const cached = await loadReferenceCache('arch_config', null).catch(() => null)
        if (cached) {
            setRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_config')
            .select('*')
        if (err) {
            console.error('[archConfig] fetch error:', err.message)
            setError(err.message)
            if (!cached) setRows([])
        } else {
            setRows(data || [])
            await saveReferenceCache('arch_config', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const getMarketConfig = useCallback((market) => {
        const row = rows.find(r => r.market === market)
        const defaults = {
            ...COST_DEFAULTS,
            base_price_sqft:          DEFAULT_BASE_PRICES[market]  ?? 0,
            add_alt_exist_base_price: ADD_ALT_BASE_PRICES[market]  ?? 0,
            add_alt_rem_base_price:   ADD_ALT_BASE_PRICES[market]  ?? 0,
            add_alt_add_base_price:   ADD_ALT_BASE_PRICES[market]  ?? 0,
        }
        if (!row) return defaults
        // Merge DB row, but never let null/0/empty overwrite hardcoded defaults
        const merged = { ...defaults }
        for (const key of Object.keys(row)) {
            const val = row[key]
            if (val != null && val !== '' && val !== 0) {
                merged[key] = val
            }
        }
        return merged
    }, [rows])

    const saveMarketConfig = useCallback(async (market, updates) => {
        const existing = rows.find(r => r.market === market)
        const payload  = { ...updates, market, updated_at: new Date().toISOString() }

        if (existing?.id) {
            const { data, error: err } = await supabase
                .from('arch_config')
                .update(payload)
                .eq('id', existing.id)
                .select()
                .single()
            if (err) throw new Error(err.message)
            setRows(prev => prev.map(r => r.market === market ? data : r))
            await clearReferenceCache('arch_config', null).catch(() => {})
            return data
        }

        const { data, error: err } = await supabase
            .from('arch_config')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => [...prev, data])
        await clearReferenceCache('arch_config', null).catch(() => {})
        return data
    }, [rows])

    // Derive marketOptions from DB rows; merge with hardcoded MARKET_OPTIONS so new markets
    // appear in the UI even before they have a DB row.
    const marketOptions = useMemo(() => {
        if (rows.length === 0) return MARKET_OPTIONS
        const dbKeys = new Set(rows.map(r => r.market))
        const dbOptions = rows.map(row => {
            const seed = MARKET_OPTIONS.find(m => m.key === row.market)
            const labelKey = seed?.labelKey || row.label || row.market
            const [stateStr, typeStr] = row.market.split('_')
            return {
                key:   row.market,
                state: stateStr || row.market,
                type:  typeStr  || '',
                labelKey,
            }
        })
        const missing = MARKET_OPTIONS.filter(m => !dbKeys.has(m.key))
        return [...dbOptions, ...missing].sort((a, b) => a.key.localeCompare(b.key))
    }, [rows])

    const createMarket = useCallback(async (marketKey, marketLabel) => {
        const payload = { market: marketKey, updated_at: new Date().toISOString() }
        const { data, error: err } = await supabase
            .from('arch_config')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        setRows(prev => [...prev, data])
        await clearReferenceCache('arch_config', null).catch(() => {})
        return data
    }, [])

    const deleteMarket = useCallback(async (marketKey) => {
        const { error: err } = await supabase
            .from('arch_config')
            .delete()
            .eq('market', marketKey)
        if (err) throw new Error(err.message)
        setRows(prev => prev.filter(r => r.market !== marketKey))
        await clearReferenceCache('arch_config', null).catch(() => {})
    }, [])

    return { rows, loading, error, marketOptions, getMarketConfig, saveMarketConfig, createMarket, deleteMarket, reload: load }
}

/**
 * useArchDifficultyLevels — loads difficulty/complexity levels from `arch_difficulty_levels`.
 * Falls back to the hardcoded DIFFICULTY_LEVELS array if the table doesn't exist or returns empty.
 */
export function useArchDifficultyLevels() {
    const [levels,  setLevels]  = useState(DIFFICULTY_LEVELS)
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        const cached = await loadReferenceCache('arch_difficulty_levels', null).catch(() => null)
        if (cached && cached.data && cached.data.length > 0) {
            setLevels(cached.data.map(r => ({
                id:    r.id,
                value: Number(r.value),
                label: r.label,
                desc:  r.description,
            })))
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_difficulty_levels')
            .select('*')
            .order('sort_order', { ascending: true })
        if (err) {
            console.error('[archDifficulty] fetch error:', err.message)
            setError(err.message)
            // keep the static fallback already in state
        } else {
            if (data && data.length > 0) {
                setLevels(data.map(r => ({
                    id:    r.id,
                    value: Number(r.value),
                    label: r.label,
                    desc:  r.description,
                })))
            }
            await saveReferenceCache('arch_difficulty_levels', data || [], null).catch(() => {})
        }
        setLoading(false)
    }, [])

    useEffect(() => { load() }, [load])

    const upsertLevel = useCallback(async ({ id, value, label, desc, sort_order }) => {
        const payload = {
            value:       Number(value),
            label:       label.trim(),
            description: (desc || '').trim(),
            sort_order:  Number(sort_order) || 0,
            updated_at:  new Date().toISOString(),
        }
        if (id) {
            const { data, error: err } = await supabase
                .from('arch_difficulty_levels')
                .update(payload)
                .eq('id', id)
                .select()
                .single()
            if (err) throw new Error(err.message)
            const mapped = { id: data.id, value: Number(data.value), label: data.label, desc: data.description }
            setLevels(prev => prev.map(l => l.id === id ? mapped : l))
            await clearReferenceCache('arch_difficulty_levels', null).catch(() => {})
            return mapped
        }
        const { data, error: err } = await supabase
            .from('arch_difficulty_levels')
            .insert(payload)
            .select()
            .single()
        if (err) throw new Error(err.message)
        const mapped = { id: data.id, value: Number(data.value), label: data.label, desc: data.description }
        setLevels(prev => {
            const next = [...prev, mapped]
            return next.sort((a, b) => a.value - b.value)
        })
        await clearReferenceCache('arch_difficulty_levels', null).catch(() => {})
        return mapped
    }, [])

    const removeLevel = useCallback(async (id) => {
        setLevels(prev => {
            if (prev.length <= 1) throw new Error('Must keep at least one difficulty level')
            return prev
        })
        const { error: err } = await supabase
            .from('arch_difficulty_levels')
            .delete()
            .eq('id', id)
        if (err) throw new Error(err.message)
        setLevels(prev => prev.filter(l => l.id !== id))
        await clearReferenceCache('arch_difficulty_levels', null).catch(() => {})
    }, [])

    return { levels, loading, error, reload: load, upsertLevel, removeLevel }
}

// ═══════════════════════════════════════════════════════════════════════════════
//  NEW CONFIG HOOKS — Minimum Discipline Costs & Global Config
// ═══════════════════════════════════════════════════════════════════════════════

const ALL_DEFAULT_ROLES = {
    direct:      DIRECT_COST_ROLES,
    certifier:   CERTIFIER_ROLES,
    commission:  COMMISSION_ROLES,
    operating:   OPERATING_ROLES,
    franchise:   FRANCHISE_ROLES,
    profit:      PROFIT_ROLES,
    contingency: CONTINGENCY_ROLES,
}

/**
 * useArchCostRoles — generic cost role loader/manager.
 * Supports arbitrary groups, per-role toggles, drag/drop sort_order, and custom group titles.
 */
export function useArchCostRoles(market) {
    const [rolesByType, setRolesByType] = useState({ ...ALL_DEFAULT_ROLES })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const normalizeRow = useCallback((r) => ({
        roleKey:    r.role_key,
        labelKey:   r.label_key,
        pct:        Number(r.pct),
        is_enabled: r.is_enabled !== false,
        sort_order: Number(r.sort_order) || 0,
        group_title: r.group_title || null,
        discipline: r.discipline || 'ALL',
    }), [])

    const applyRows = useCallback((rows) => {
        const byType = {}
        for (const r of rows) {
            const type = r.role_type
            if (!byType[type]) byType[type] = []
            byType[type].push(normalizeRow(r))
        }
        for (const type of Object.keys(byType)) {
            byType[type].sort((a, b) => a.sort_order - b.sort_order)
        }
        setRolesByType(() => {
            const next = {}
            // Always start from clean defaults so stale data from previous market never persists
            for (const type of Object.keys(ALL_DEFAULT_ROLES)) {
                next[type] = ALL_DEFAULT_ROLES[type].map(r => ({ ...r }))
            }
            // Overwrite with DB data for the current market
            for (const type of Object.keys(byType)) {
                next[type] = byType[type]
            }
            return next
        })
    }, [normalizeRow])

    const load = useCallback(async () => {
        setLoading(true); setError(null)
        const cacheKey = market || 'default'
        const cached = await loadReferenceCache('arch_cost_roles', cacheKey).catch(() => null)
        if (cached && cached.data && cached.data.length > 0) {
            applyRows(cached.data)
            setLoading(false)
        }
        let query = supabase
            .from('arch_cost_roles')
            .select('*')
            .order('role_type', { ascending: true })
            .order('sort_order', { ascending: true })
        if (market) query = query.eq('market', market)
        const { data, error: err } = await query
        if (err) {
            console.error('[archCostRoles] fetch error:', err.message)
            setError(err.message)
        } else if (data && data.length > 0) {
            applyRows(data)
            await saveReferenceCache('arch_cost_roles', data, cacheKey).catch(() => {})
        }
        setLoading(false)
    }, [applyRows, market])

    useEffect(() => { load() }, [load])

    const saveRole = useCallback(async (roleType, roleKey, updates) => {
        let q = supabase.from('arch_cost_roles').select('id')
            .eq('role_type', roleType)
            .eq('role_key', roleKey)
        if (market) q = q.eq('market', market)
        const { data: existing } = await q.single()
        const payload = {
            pct:        updates.pct != null ? Number(updates.pct) : undefined,
            label_key:  updates.label_key || undefined,
            is_enabled: updates.is_enabled != null ? Boolean(updates.is_enabled) : undefined,
            sort_order: updates.sort_order != null ? Number(updates.sort_order) : undefined,
            group_title: updates.group_title != null ? (updates.group_title || null) : undefined,
            discipline: updates.discipline != null ? (updates.discipline || 'ALL') : undefined,
            updated_at: new Date().toISOString(),
        }
        Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k])
        const cacheKey = market || 'default'

        if (existing?.id) {
            const { data, error: err } = await supabase.from('arch_cost_roles').update(payload).eq('id', existing.id).select().single()
            if (err) throw new Error(err.message)
            await clearReferenceCache('arch_cost_roles', cacheKey).catch(() => {})
            return data
        }
        const insertPayload = { role_type: roleType, role_key: roleKey, ...payload }
        if (market) insertPayload.market = market
        const { data, error: err } = await supabase.from('arch_cost_roles').insert(insertPayload).select().single()
        if (err) throw new Error(err.message)
        await clearReferenceCache('arch_cost_roles', cacheKey).catch(() => {})
        return data
    }, [market])

    const createRole = useCallback(async (roleType, roleKey, data) => {
        const current = rolesByType[roleType] || []
        const maxSort = current.reduce((m, r) => Math.max(m, r.sort_order || 0), 0)
        const payload = {
            role_type: roleType,
            role_key: roleKey,
            label_key: data.label_key || roleKey,
            pct: Number(data.pct) || 0,
            is_enabled: data.is_enabled !== false,
            sort_order: maxSort + 1,
            group_title: data.group_title || null,
            discipline: data.discipline || 'ALL',
        }
        if (market) payload.market = market
        const { data: inserted, error: err } = await supabase.from('arch_cost_roles').insert(payload).select().single()
        if (err) throw new Error(err.message)
        await clearReferenceCache('arch_cost_roles', market || 'default').catch(() => {})
        return inserted
    }, [rolesByType, market])

    const deleteRole = useCallback(async (roleType, roleKey) => {
        let q = supabase.from('arch_cost_roles').delete()
            .eq('role_type', roleType)
            .eq('role_key', roleKey)
        if (market) q = q.eq('market', market)
        const { error: err } = await q
        if (err) throw new Error(err.message)
        const cacheKey = market || 'default'
        await clearReferenceCache('arch_cost_roles', cacheKey).catch(() => {})
        // Renumber remaining roles in the group
        let remQ = supabase.from('arch_cost_roles').select('*').eq('role_type', roleType)
        if (market) remQ = remQ.eq('market', market)
        const remaining = await remQ.order('sort_order', { ascending: true })
        if (remaining.data) {
            for (let i = 0; i < remaining.data.length; i++) {
                await supabase.from('arch_cost_roles')
                    .update({ sort_order: i + 1 })
                    .eq('id', remaining.data[i].id)
            }
        }
        await clearReferenceCache('arch_cost_roles', cacheKey).catch(() => {})
    }, [market])

    const updateGroupTitle = useCallback(async (roleType, title) => {
        let q = supabase.from('arch_cost_roles')
            .update({ group_title: title || null, updated_at: new Date().toISOString() })
            .eq('role_type', roleType)
        if (market) q = q.eq('market', market)
        const { error: err } = await q
        if (err) throw new Error(err.message)
        await clearReferenceCache('arch_cost_roles', market || 'default').catch(() => {})
    }, [market])

    return {
        rolesByType,
        allRoleTypes: Object.keys(rolesByType),
        loading,
        error,
        reload: load,
        saveRole,
        createRole,
        deleteRole,
        updateGroupTitle,
    }
}

/**
 * useArchDisciplinePresets — loads discipline split presets from arch_discipline_presets.
 * Falls back to hardcoded SCOPE_OPTION_TO_PCT.
 */
export function useArchDisciplinePresets() {
    const [presets, setPresets] = useState(SCOPE_OPTION_TO_PCT)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const applyRows = useCallback((rows) => {
        const map = {}
        for (const r of rows) {
            map[r.scope_key] = {
                archPct: Number(r.arch_pct),
                structPct: Number(r.struct_pct),
                mepPct: Number(r.mep_pct),
                civilPct: Number(r.civil_pct),
                adminPct: Number(r.admin_pct),
            }
        }
        setPresets({ ...SCOPE_OPTION_TO_PCT, ...map })
    }, [])

    const load = useCallback(async () => {
        setLoading(true); setError(null)
        const cached = await loadReferenceCache('arch_discipline_presets', null).catch(() => null)
        if (cached && cached.data && cached.data.length > 0) {
            applyRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase
            .from('arch_discipline_presets')
            .select('*')
        if (err) {
            console.error('[archDisciplinePresets] fetch error:', err.message)
            setError(err.message)
        } else if (data && data.length > 0) {
            applyRows(data)
            await saveReferenceCache('arch_discipline_presets', data, null).catch(() => {})
        }
        setLoading(false)
    }, [applyRows])

    useEffect(() => { load() }, [load])

    const savePreset = useCallback(async (scopeKey, pcts) => {
        const payload = {
            scope_key: scopeKey,
            arch_pct: Number(pcts.archPct),
            struct_pct: Number(pcts.structPct),
            mep_pct: Number(pcts.mepPct),
            civil_pct: Number(pcts.civilPct),
            admin_pct: Number(pcts.adminPct),
            updated_at: new Date().toISOString(),
        }
        const { data: existing } = await supabase.from('arch_discipline_presets').select('id').eq('scope_key', scopeKey).single()
        if (existing?.id) {
            const { data, error: err } = await supabase.from('arch_discipline_presets').update(payload).eq('id', existing.id).select().single()
            if (err) throw new Error(err.message)
            setPresets(prev => ({ ...prev, [scopeKey]: { archPct: pcts.archPct, structPct: pcts.structPct, mepPct: pcts.mepPct, civilPct: pcts.civilPct, adminPct: pcts.adminPct } }))
            await clearReferenceCache('arch_discipline_presets', null).catch(() => {})
            return data
        }
        const { data, error: err } = await supabase.from('arch_discipline_presets').insert(payload).select().single()
        if (err) throw new Error(err.message)
        setPresets(prev => ({ ...prev, [scopeKey]: { archPct: pcts.archPct, structPct: pcts.structPct, mepPct: pcts.mepPct, civilPct: pcts.civilPct, adminPct: pcts.adminPct } }))
        await clearReferenceCache('arch_discipline_presets', null).catch(() => {})
        return data
    }, [])

    return { presets, loading, error, reload: load, savePreset }
}

/**
 * useArchScopeMultipliers — loads scope multipliers from arch_scope_multipliers.
 * Falls back to hardcoded DISCIPLINE_SCOPE_MULTIPLIERS.
 */
export function useArchScopeMultipliers() {
    const [multipliers, setMultipliers] = useState(DISCIPLINE_SCOPE_MULTIPLIERS)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const applyRows = useCallback((rows) => {
        const map = {}
        for (const r of rows) {
            map[r.market] = r.multipliers || {}
        }
        setMultipliers(prev => {
            const next = {}
            for (const m of Object.keys(prev)) next[m] = { ...prev[m], ...(map[m] || {}) }
            return next
        })
    }, [])

    const load = useCallback(async () => {
        setLoading(true); setError(null)
        const cached = await loadReferenceCache('arch_scope_multipliers', null).catch(() => null)
        if (cached && cached.data && cached.data.length > 0) {
            applyRows(cached.data)
            setLoading(false)
        }
        const { data, error: err } = await supabase.from('arch_scope_multipliers').select('*')
        if (err) {
            console.error('[archScopeMultipliers] fetch error:', err.message)
            setError(err.message)
        } else if (data && data.length > 0) {
            applyRows(data)
            await saveReferenceCache('arch_scope_multipliers', data, null).catch(() => {})
        }
        setLoading(false)
    }, [applyRows])

    useEffect(() => { load() }, [load])

    const saveMultiplier = useCallback(async (market, scopeKey, value) => {
        const { data: existing } = await supabase.from('arch_scope_multipliers').select('id,multipliers').eq('market', market).single()
        const currentMults = existing?.multipliers || {}
        const newMults = { ...currentMults, [scopeKey]: Number(value) }
        const payload = { market, multipliers: newMults, updated_at: new Date().toISOString() }
        if (existing?.id) {
            const { data, error: err } = await supabase.from('arch_scope_multipliers').update(payload).eq('id', existing.id).select().single()
            if (err) throw new Error(err.message)
            setMultipliers(prev => ({ ...prev, [market]: { ...prev[market], [scopeKey]: Number(value) } }))
            await clearReferenceCache('arch_scope_multipliers', null).catch(() => {})
            return data
        }
        const { data, error: err } = await supabase.from('arch_scope_multipliers').insert(payload).select().single()
        if (err) throw new Error(err.message)
        setMultipliers(prev => ({ ...prev, [market]: { ...prev[market], [scopeKey]: Number(value) } }))
        await clearReferenceCache('arch_scope_multipliers', null).catch(() => {})
        return data
    }, [])

    return { multipliers, loading, error, reload: load, saveMultiplier }
}

/**
 * useArchMinFloors — reads min_floor_* from arch_config rows.
 * Falls back to hardcoded ADD_ALT_MIN_FLOORS.
 */
export function useArchMinFloors(rows) {
    return useMemo(() => {
        const get = (market) => {
            const row = (rows || []).find(r => r.market === market)
            return {
                exist: Number(row?.min_floor_exist ?? ADD_ALT_MIN_FLOORS.exist),
                remodel: Number(row?.min_floor_remodel ?? ADD_ALT_MIN_FLOORS.remodel),
                adicion: Number(row?.min_floor_adicion ?? ADD_ALT_MIN_FLOORS.adicion),
            }
        }
        return { getMinFloors: get }
    }, [rows])
}
